import React, { useState } from 'react';
import { ethers } from 'ethers';
import Card from '../Common/Card';
import Button from '../Common/Button';
import Input from '../Common/Input';
import PlanCard from './PlanCard';
import RegisterSuccessModal from './RegisterSuccessModal';
import { useWeb3 } from '../../context/Web3Context';
import { parseUSDT, formatUSDT } from '../../utils/format';
import { notify, removeNotify } from '../../utils/notify';
import { parseContractError } from '../../utils/errors';
import { AMSG_CONTRACT_ADDRESS } from '../../config/contract';

const InvestForm = ({ minInvestment, multiple, onSuccess, isRegistered, refUrlId }) => {
  const { contract, usdtContract, account, onCorrectNetwork, switchToCorrectNetwork, refreshUserId } = useWeb3();

  const [amount, setAmount] = useState('');
  const [plan, setPlan] = useState(0);
  const [referrerId, setReferrerId] = useState(refUrlId || '');
  const [busy, setBusy] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successPlan, setSuccessPlan] = useState(0);
  const [successAmount, setSuccessAmount] = useState('0');

  const submit = async () => {
    if (!contract || !usdtContract) {
      notify.error('Wallet not connected');
      return;
    }
    if (!onCorrectNetwork) {
      notify.warning('Switching to correct network...');
      const ok = await switchToCorrectNetwork();
      if (!ok) return;
      // Give the wallet a moment to settle the chain change before signing.
      await new Promise((r) => setTimeout(r, 600));
      notify.info('Network ready. Continuing...');
    }

    const parsedAmount = parseUSDT(amount);
    if (parsedAmount.lte(0)) {
      notify.error('Enter a valid amount');
      return;
    }

    const minBN = ethers.BigNumber.from(minInvestment || '0');
    if (parsedAmount.lt(minBN)) {
      notify.error('Amount below minimum (' + formatUSDT(minBN) + ' USDT)');
      return;
    }

    // Mirror the contract's multiple check so we fail fast with a clear message
    const mulBN = ethers.BigNumber.from(multiple || '0');
    if (mulBN.gt(0) && !parsedAmount.eq(minBN) && !parsedAmount.mod(mulBN).isZero()) {
      notify.error('Amount must be a multiple of ' + formatUSDT(mulBN) + ' USDT');
      return;
    }

    setBusy(true);
    let loadingId = null;
    try {
      const balance = await usdtContract.balanceOf(account);
      if (balance.lt(parsedAmount)) {
        notify.error('Insufficient USDT balance. You have ' + formatUSDT(balance) + ' USDT.');
        setBusy(false);
        return;
      }

      const allowance = await usdtContract.allowance(account, AMSG_CONTRACT_ADDRESS);
      if (allowance.lt(parsedAmount)) {
        loadingId = notify.loading('Approving USDT...');
        const approveTx = await usdtContract.approve(AMSG_CONTRACT_ADDRESS, ethers.constants.MaxUint256);
        await approveTx.wait();
        removeNotify(loadingId);
        loadingId = null;
        notify.success('USDT approved');
      }

      loadingId = notify.loading('Sending investment transaction...');
      let tx;
      const wasRegistration = !isRegistered;
      if (wasRegistration) {
        const refId = referrerId ? parseInt(referrerId, 10) : 0;
        tx = await contract.registerAndInvest(refId, parsedAmount, plan);
      } else {
        tx = await contract.invest(parsedAmount, plan);
      }
      await tx.wait();
      removeNotify(loadingId);
      loadingId = null;

      if (wasRegistration) {
        await refreshUserId();
        setSuccessPlan(plan);
        setSuccessAmount(parsedAmount.toString());
        setSuccessOpen(true);
      } else {
        notify.success('Investment successful!');
      }
      setAmount('');
      onSuccess && onSuccess();
    } catch (err) {
      if (loadingId) removeNotify(loadingId);
      notify.error(parseContractError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={isRegistered ? 'New Investment' : 'Register & Invest'} subtitle="Lock USDT into a staking plan">
      {!isRegistered && (
        <Input
          label="Referrer ID (optional)"
          value={referrerId}
          onChange={(e) => setReferrerId(e.target.value.replace(/[^0-9]/g, ''))}
          placeholder="Enter referrer ID"
          helper="Leave blank to use the default referrer"
        />
      )}

      <div className="form-group">
        <label className="form-label">Select Staking Plan</label>
        <PlanCard selected={plan} onChange={setPlan} />
      </div>

      <Input
        label="Amount (USDT)"
        value={amount}
        onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
        placeholder="0.00"
        suffix="USDT"
        type="text"
        helper={`Min: ${formatUSDT(minInvestment || '0')} USDT · Multiple: ${formatUSDT(multiple || '0')} USDT`}
      />

      <Button
        variant="primary"
        size="lg"
        block
        onClick={submit}
        loading={busy}
        disabled={!account}
      >
        {!account ? 'Connect Wallet' : (isRegistered ? 'Stake Now' : 'Register & Stake')}
      </Button>

      <RegisterSuccessModal
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        plan={successPlan}
        amount={successAmount}
      />
    </Card>
  );
};

export default InvestForm;
